import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import MIcon from '../MIcon';
import MSwipeableDrawerMobile from '../MSwipeableDrawerMobile';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import getColorFromVariant from '../../../utils/getColorFromVariant';

/**
 * Renders a mobile menu component encapsulated within a swipeable drawer.
 * It dynamically generates a list of options based on provided props. Each option can be customized
 * with icons, text variants for color theming, and additional captions.
 *
 * @param {object} props - The component's props.
 * @param {boolean} [props.open=false] - Controls the visibility of the mobile menu.
 * @param {string} [props.optionName='text'] - Specifies the object property name to use for the menu option text.
 * @param {Array} [props.options=[]] - An array of objects representing the menu options. Each option object may contain `id`, `handler` function, `icon`, `iconVariant`, `filled`, `textVariant`, and `caption`.
 * @param {string} [props.title=''] - The title displayed at the top of the mobile menu.
 * @param {function} props.handleOpen - A function to execute when the menu is opened.
 * @param {function} props.handleClose - A function to execute when the menu is closed.
 * @returns {JSX.Element} A Material-UI List component within a `MSwipeableDrawerMobile`, containing ListItem components for each provided option.
 */
const MMobileMenu = (props) => {
  const { open = false, options = [], optionName = 'text', title = '', handleOpen, handleClose } = props;
  const theme = useTheme();
  const { isRegularMobile } = useBreakpoints();

  if (!options) {
    return;
  }

  return (
    <MSwipeableDrawerMobile title={title} open={open} onClose={handleClose} onOpen={handleOpen}>
      <List>
        {options.map((opt, idx) => {
          const color = getColorFromVariant(theme, opt.textVariant || 'neutral');

          if (opt?.divider) {
            return;
          }

          return (
            <ListItem key={`${opt.id}-${idx}`} disablePadding>
              <ListItemButton
                disableGutters={isRegularMobile}
                disabled={!!opt.disabled}
                onClick={(e) => {
                  handleClose();
                  opt.handler(e);
                }}
              >
                {!!opt?.icon && (
                  <ListItemIcon>
                    <MIcon icon={opt.icon} variant={opt.iconVariant} filled={opt.filled || true} />
                  </ListItemIcon>
                )}
                <ListItemText primary={opt[optionName]} secondary={opt.caption} primaryTypographyProps={{ color }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </MSwipeableDrawerMobile>
  );
};

MMobileMenu.propTypes = {
  open: PropTypes.bool,
  optionName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      text: PropTypes.string,
      textVariant: PropTypes.string,
      caption: PropTypes.string,
      icon: PropTypes.string,
      iconVariant: PropTypes.string,
      filled: PropTypes.bool,
      divider: PropTypes.bool,
      handler: PropTypes.func
    })
  ).isRequired,
  title: PropTypes.string,
  handleOpen: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired
};

export default MMobileMenu;
